import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Toast, Loading, Dialog } from "@xp-design/mobile";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { getBenefitInfo } from "@/api/configPage.ts";
import { winningRecord } from "@/pages/par-marketing/rankingLuckyRecord/ajax";
import PopupCom from "@/component/popupCom";
import {
  goToPage,
  getBrowserEnv,
  getChannel,
  getClientEnv,
} from "@/utils/tools";
import { goToNativePage, debounce, goToPageV2 } from "@/utils/common";
import img_empty from "@/assets/img_empty.png";
import { trackGdp } from "@/utils/gio-report";
import icon_close from "@/pages/par-marketing/configPage/assets/icon_close.png";
import styles from "./index.module.less";

interface PrizeRecordPopupProps {
  moduleJsonData?: any;
  activityInfo?: any;
}

export interface PrizeRecordPopupRef {
  show: (activityCode: string) => void;
}

const PrizeRecordPopup = forwardRef<PrizeRecordPopupRef, PrizeRecordPopupProps>(
  ({ moduleJsonData, activityInfo }, ref) => {
    const popupRef = useRef<any>(null);
    const [maskShow, setMaskShow] = useState(false);
    const [recordList, setRecordList] = useState<any[]>([]);
    const [copyDialogVisible, setCopyDialogVisible] = useState(false);
    const [copyDialogInfo, setCopyDialogInfo] = useState({
      productName: "",
      benefitType: 0,
      cardKey: "",
      cardNo: "",
      redeemLink: "",
      ruleDesc: "",
    });
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(100);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [currentActivityCode, setCurrentActivityCode] = useState("");
    const contentRef = useRef<HTMLDivElement>(null);

    // 获取中奖列表
    const getList = async (activityCode: string, currentPageNo: number = 1) => {
      if (!hasMore && currentPageNo > 1) {
        return;
      }
      try {
        setIsLoading(true);
        if (currentPageNo === 1) {
          Loading.show("加载中...");
        }

        const res = await winningRecord({
          activityCode,
          pageNo: currentPageNo,
          pageSize: pageSize,
        });

        if (res.code === 200) {
          const newArr =
            currentPageNo === 1
              ? res.data.records
              : [...recordList, ...res.data.records];
          setRecordList(newArr);

          if (currentPageNo === 1) {
            trackGdp("P20190", "B001", {
              trackName: "充电_营销奖品记录页_页面访问",
              origin: getChannel(),
              userType: "",
              activityId: activityCode,
            });
          }

          // 检查是否还有更多数据
          setHasMore(res.data.total > newArr.length);
          setPageNo(currentPageNo + 1);
        } else {
          Toast.show({
            content: res.msg,
          });
        }
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        console.error("获取中奖记录失败:", error);
      } finally {
        if (currentPageNo === 1) {
          Loading.hide();
        }
      }
    };

    // 滚动加载更多
    const handleScroll = debounce(() => {
      const contentElement = contentRef.current;
      if (contentElement) {
        const scrollTop = contentElement.scrollTop;
        const scrollHeight = contentElement.scrollHeight;
        const clientHeight = contentElement.clientHeight;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          if (hasMore && !isLoading && currentActivityCode) {
            getList(currentActivityCode, pageNo);
          }
        }
      }
    }, 300);

    useEffect(() => {
      const contentElement = contentRef.current;
      if (contentElement) {
        contentElement.addEventListener("scroll", handleScroll);
      }
      return () => {
        if (contentElement) {
          contentElement.removeEventListener("scroll", handleScroll);
        }
      };
    }, [hasMore, isLoading, currentActivityCode, pageNo]);

    // 跳转奖品详情
    const goDetail = async (item: any) => {
      let wxUrl = "";
      let appUrl = "";
      let httpUrl = "";
      let env = getClientEnv();

      switch (item.productType) {
        case "11":
        case "5":
          wxUrl = "/charging/pages/voucher-list/index?tabIndex=0";
          appUrl = "xiaopeng://charging/card?type=0";
          if (item.chargePackType == "07") {
            wxUrl = "/charging/pages/voucher-list/index?tabIndex=2";
            appUrl = "xiaopeng://charging/card?type=2";
          }
          break;
        case "1":
        case "6":
          wxUrl = "/charging/pages/voucher-list/index?tabIndex=1";
          appUrl = "xiaopeng://charging/card?type=1";
          break;
        case "7":
          httpUrl = window.origin + "/apps/charge-web/charge-vip.html";
          break;
        case "4":
          httpUrl = item.otherActivityUrl;
          break;
        case "12":
          if (!item.productCaseId) {
            Toast.show({
              content: "奖品发放中，请稍后重试",
            });
          } else {
            httpUrl =
              window.origin +
              "/apps/charge-web/charge-vip.html#/exchange-rights?benefitInfoId=" +
              item.productCaseId;
          }
          break;
        case "8":
          wxUrl = "/charging/pages/voucher-list/index?tabIndex=3";
          appUrl = "xiaopeng://charging/card?type=3";
          break;
        case "14":
          httpUrl =
            env == "product"
              ? "https://mall.xiaopeng.com/coupon/list"
              : "https://pmall.cs-pre.xiaopeng.com/coupon/list";
          appUrl = "xiaopeng://account/coupons?cate=ESHOP_COUPON";
          break;
        default:
          break;
      }

      let channel = getChannel();
      if (["iosBrowser", "browser"].includes(channel) && appUrl) {
        const url = `https://app.xiaopeng.com/activity/openurl?url=${encodeURIComponent(appUrl)}`;
        window.location.href = url;
      } else {
        if (httpUrl) {
          if (
            httpUrl.includes(window.location.origin) ||
            item.productType == "4" ||
            item.productType == "14"
          ) {
            window.location.href = httpUrl;
          } else {
            goToPageV2({ url: httpUrl });
          }
        } else {
          goToNativePage({
            mini: wxUrl,
            app: appUrl,
          });
        }
      }
    };

    const gotoDetail = (item: any) => {
      goDetail(item);
      trackGdp("P20190", "B002", {
        trackName: "充电_营销奖品记录页_按钮点击",
        origin: getChannel(),
        userType: "",
        activityId: currentActivityCode,
        prize_type: item.productType,
        prize_name: item.productName,
        prize_id: item.productCode,
      });
    };

    const handleCopy = () => {
      Toast.show({
        content: "已复制到剪贴板",
      });
    };

    // 显示弹窗
    const showPopup = (activityCode: string) => {
      // 重置状态
      setCurrentActivityCode(activityCode);
      setPageNo(1);
      setRecordList([]);
      setHasMore(true);
      setIsLoading(false);
      setCopyDialogVisible(false);

      setMaskShow(true);
      popupRef.current?.onOpen();

      // 加载数据
      getList(activityCode, 1);
    };

    // 关闭回调
    const onCloseCallBack = async () => {
      setTimeout(() => {
        setMaskShow(false);
        // 清理数据
        setRecordList([]);
        setPageNo(1);
        setHasMore(true);
        setCurrentActivityCode("");
      }, 500);
    };

    useImperativeHandle(ref, () => ({
      show: showPopup,
    }));

    // 获取弹窗背景图 - 使用默认背景或者从moduleJsonData获取
    const getPopupBackUrl = () => {
      // 可以根据实际需要从moduleJsonData中获取背景图配置
      return moduleJsonData?.visual?.prizeSetting?.[2]?.content?.[0]?.val || "";
    };

    return (
      <>
        <PopupCom
          ref={popupRef}
          height="80%"
          bacUrl={getPopupBackUrl()}
          onCloseCallBack={onCloseCallBack}
          title="中奖记录"
          children={
            <div className={styles.prizeRecordContent}>
              <div className={styles.scrollContainer} ref={contentRef}>
                {recordList.map((item: any, i) => (
                  <div className={styles.recordCard} key={`prizeRecord${i}`}>
                    <div className={styles.cardLeft}>
                      <div className={styles.prizeName}>{item.productName}</div>
                      <div className={styles.prizeTime}>{item.raffleTime}</div>
                    </div>
                    <div
                      className={styles.cardRight}
                      onClick={() => gotoDetail(item)}
                    >
                      去看看
                    </div>
                  </div>
                ))}

                {recordList.length === 0 && !isLoading && (
                  <div className={styles.noData}>
                    <img src={img_empty} alt="暂无数据" />
                    <div>暂无奖品记录</div>
                  </div>
                )}

                {!hasMore && recordList.length > 0 && (
                  <div className={styles.endMessage}>没有更多数据了</div>
                )}

                {isLoading && pageNo > 1 && (
                  <div className={styles.loadingMessage}>加载中...</div>
                )}
              </div>
            </div>
          }
        />

        {maskShow && <div className={styles.prizeMask}></div>}

        {/* 复制弹窗 */}
        <Dialog
          visible={copyDialogVisible}
          onClose={() => setCopyDialogVisible(false)}
          getContainer={null}
          closeOnMaskClick
          bodyClassName="customDialogBody"
          content={
            <div className={styles.dialogContent}>
              <div className={styles.copyDialog}>
                <div className={styles.cTitle}>
                  {copyDialogInfo.productName}
                </div>
                <div className={styles.p1}>1.复制兑换链接至手机浏览器打开</div>
                <div className={styles.copyBox}>
                  <div className={styles.code}>
                    {copyDialogInfo.redeemLink || "-"}
                  </div>
                  <CopyToClipboard
                    text={copyDialogInfo.redeemLink}
                    onCopy={handleCopy}
                  >
                    <div className={styles.copyBtn}>复制链接</div>
                  </CopyToClipboard>
                </div>
                <div className={styles.p1}>
                  2.复制卡号{copyDialogInfo.benefitType === 2 && <>、卡密</>}
                  前往兑换链接进行兑换
                </div>
                <div className={styles.copyBox}>
                  <div className={styles.code}>
                    {copyDialogInfo.cardNo || "-"}
                  </div>
                  <CopyToClipboard
                    text={copyDialogInfo.cardNo}
                    onCopy={handleCopy}
                  >
                    <div className={styles.copyBtn}>复制卡号</div>
                  </CopyToClipboard>
                </div>
                {copyDialogInfo.benefitType === 2 && (
                  <div className={styles.copyBox}>
                    <div className={styles.code}>
                      {copyDialogInfo.cardKey || "-"}
                    </div>
                    <CopyToClipboard
                      text={copyDialogInfo.cardKey}
                      onCopy={handleCopy}
                    >
                      <div className={styles.copyBtn}>复制卡密</div>
                    </CopyToClipboard>
                  </div>
                )}
                <div className={styles.p2}>使用规则说明</div>
                <div className={styles.p3}>{copyDialogInfo.ruleDesc}</div>
              </div>
              <div className={styles.closeOuter}>
                <img
                  className={styles.iconClose}
                  src={icon_close}
                  onClick={() => setCopyDialogVisible(false)}
                  alt="关闭"
                />
              </div>
            </div>
          }
        />
      </>
    );
  },
);

PrizeRecordPopup.displayName = "PrizeRecordPopup";

export default PrizeRecordPopup;
